import type {
  CanvasIconEntity,
  CanvasPoint,
  CircleHole,
  Dimension,
  MoveState,
  Shape,
} from './types';

export type DuplicateEntitiesResult = {
  entityIds: string[];
  shapes: Shape[];
  dimensions: Dimension[];
  iconEntities: CanvasIconEntity[];
};

const clonePoint = (point: CanvasPoint): CanvasPoint => ({ x: point.x, y: point.y });
const translatePoint = (point: CanvasPoint, deltaX: number, deltaY: number): CanvasPoint => ({
  x: point.x + deltaX,
  y: point.y + deltaY,
});
const clonePointArray = <T extends CanvasPoint>(points?: T[]) =>
  points?.map((point) => ({ ...point }));
const translatePointArray = <T extends CanvasPoint>(points: T[] | undefined, deltaX: number, deltaY: number) =>
  points?.map((point) => ({
    ...point,
    x: point.x + deltaX,
    y: point.y + deltaY,
  }));
const translateCoordinateArray = (points: number[], deltaX: number, deltaY: number) =>
  points.map((value, index) => (index % 2 === 0 ? value + deltaX : value + deltaY));

const cloneCircleHole = (hole: CircleHole): CircleHole => ({
  center: clonePoint(hole.center),
  radius: hole.radius,
});
const translateCircleHole = (hole: CircleHole, deltaX: number, deltaY: number): CircleHole => ({
  center: translatePoint(hole.center, deltaX, deltaY),
  radius: hole.radius,
});
const createEmptyDuplicateResult = (): DuplicateEntitiesResult => ({
  entityIds: [],
  shapes: [],
  dimensions: [],
  iconEntities: [],
});

export const buildMoveState = (
  mode: 'move' | 'copy',
  entityIds: string[],
  anchorPoint: CanvasPoint,
  shapeSource: Shape[],
  dimensionSource: Dimension[],
  iconSource: CanvasIconEntity[] = [],
  snapExcludedEntityIds: string[] = entityIds,
): MoveState => {
  const selectedIdSet = new Set(entityIds);

  return {
    mode,
    entityIds,
    snapExcludedEntityIds,
    anchorPoint,
    vectorLocked: false,
    shapeSnapshots: shapeSource.flatMap((shape) => {
      if (!selectedIdSet.has(shape.id)) return [];

      return [
        shape.kind === 'circle'
          ? {
              id: shape.id,
              kind: 'circle' as const,
              center: clonePoint(shape.center),
              radius: shape.radius,
            }
          : shape.kind === 'arc'
            ? {
                id: shape.id,
                kind: 'arc' as const,
                center: clonePoint(shape.center),
                radius: shape.radius,
                startAngle: shape.startAngle,
                endAngle: shape.endAngle,
              }
          : {
              id: shape.id,
              kind: shape.kind,
              points: [...shape.points],
              holes: shape.holes?.map((hole) => [...hole]),
              circleHoles: shape.circleHoles?.map(cloneCircleHole),
              lifeLineVertices: clonePointArray(shape.lifeLineVertices),
              lifeLineDesignPoints: clonePointArray(shape.lifeLineDesignPoints),
            },
      ];
    }),
    dimensionSnapshots: dimensionSource.flatMap((dimension) => {
      if (!selectedIdSet.has(dimension.id)) return [];

      return [
        {
          id: dimension.id,
          start: clonePoint(dimension.start),
          end: clonePoint(dimension.end),
          offset: dimension.offset,
        },
      ];
    }),
    iconSnapshots: iconSource.flatMap((iconEntity) => {
      if (!selectedIdSet.has(iconEntity.id)) return [];

      return [
        {
          id: iconEntity.id,
          position: clonePoint(iconEntity.position),
          size: iconEntity.size,
          rotation: iconEntity.rotation,
        },
      ];
    }),
  };
};

export const applyMoveState = (
  shapeSource: Shape[],
  dimensionSource: Dimension[],
  iconSource: CanvasIconEntity[],
  move: MoveState,
  pointer: CanvasPoint,
) => {
  const deltaX = pointer.x - move.anchorPoint.x;
  const deltaY = pointer.y - move.anchorPoint.y;

  const shapeSnapshotMap = new Map(move.shapeSnapshots.map((snapshot) => [snapshot.id, snapshot]));
  const dimensionSnapshotMap = new Map(
    move.dimensionSnapshots.map((snapshot) => [snapshot.id, snapshot]),
  );
  const iconSnapshotMap = new Map((move.iconSnapshots ?? []).map((snapshot) => [snapshot.id, snapshot]));

  const shapes = shapeSource.map((shape) => {
    const snapshot = shapeSnapshotMap.get(shape.id);
    if (!snapshot) return shape;

    if (snapshot.kind === 'circle' && shape.kind === 'circle') {
      return {
        ...shape,
        center: translatePoint(snapshot.center, deltaX, deltaY),
      };
    }

    if (snapshot.kind === 'arc' && shape.kind === 'arc') {
      return {
        ...shape,
        center: translatePoint(snapshot.center, deltaX, deltaY),
      };
    }

    if (snapshot.kind !== 'circle' && snapshot.kind !== 'arc' && shape.kind !== 'circle' && shape.kind !== 'arc') {
      return {
        ...shape,
        points: translateCoordinateArray(snapshot.points, deltaX, deltaY),
        holes: snapshot.holes?.map((hole) => translateCoordinateArray(hole, deltaX, deltaY)),
        circleHoles: snapshot.circleHoles?.map((hole) => translateCircleHole(hole, deltaX, deltaY)),
        lifeLineVertices: translatePointArray(snapshot.lifeLineVertices, deltaX, deltaY),
        lifeLineDesignPoints: translatePointArray(snapshot.lifeLineDesignPoints, deltaX, deltaY),
      };
    }

    return shape;
  });

  const dimensions = dimensionSource.map((dimension) => {
    const snapshot = dimensionSnapshotMap.get(dimension.id);
    if (!snapshot) return dimension;

    return {
      ...dimension,
      start: translatePoint(snapshot.start, deltaX, deltaY),
      end: translatePoint(snapshot.end, deltaX, deltaY),
    };
  });

  const iconEntities = iconSource.map((iconEntity) => {
    const snapshot = iconSnapshotMap.get(iconEntity.id);
    if (!snapshot) return iconEntity;

    return {
      ...iconEntity,
      position: translatePoint(snapshot.position, deltaX, deltaY),
    };
  });

  return {
    shapes,
    dimensions,
    iconEntities,
  };
};

export const duplicateEntities = (
  shapeSource: Shape[],
  dimensionSource: Dimension[],
  iconSource: CanvasIconEntity[],
  entityIds: string[],
  createPolylineId: () => string,
  createDimensionId: () => string,
  createIconId: () => string,
): DuplicateEntitiesResult => {
  if (entityIds.length === 0) {
    return createEmptyDuplicateResult();
  }

  const selectedIdSet = new Set(entityIds);
  const shapes = shapeSource.flatMap((shape) => {
    if (!selectedIdSet.has(shape.id)) return [];

    return [
      shape.kind === 'circle'
        ? {
            ...shape,
            id: createPolylineId(),
            name: `${shape.name} copia`,
          }
        : shape.kind === 'arc'
          ? {
              ...shape,
              id: createPolylineId(),
              name: `${shape.name} copia`,
              center: clonePoint(shape.center),
            }
        : {
            ...shape,
            id: createPolylineId(),
            name: `${shape.name} copia`,
            points: [...shape.points],
            holes: shape.holes?.map((hole) => [...hole]),
            circleHoles: shape.circleHoles?.map(cloneCircleHole),
            lifeLineVertices: clonePointArray(shape.lifeLineVertices),
            lifeLineDesignPoints: clonePointArray(shape.lifeLineDesignPoints),
          },
    ];
  });
  const dimensions = dimensionSource.flatMap((dimension) => {
    if (!selectedIdSet.has(dimension.id)) return [];

    return [
      {
        ...dimension,
        id: createDimensionId(),
        name: `${dimension.name} copia`,
        start: clonePoint(dimension.start),
        end: clonePoint(dimension.end),
      },
    ];
  });
  const iconEntities = iconSource.flatMap((iconEntity) => {
    if (!selectedIdSet.has(iconEntity.id)) return [];

    return [
      {
        ...iconEntity,
        id: createIconId(),
        name: `${iconEntity.name} copia`,
        position: clonePoint(iconEntity.position),
      },
    ];
  });
  const duplicatedIds = [
    ...shapes.map((shape) => shape.id),
    ...dimensions.map((dimension) => dimension.id),
    ...iconEntities.map((iconEntity) => iconEntity.id),
  ];

  return {
    entityIds: duplicatedIds,
    shapes,
    dimensions,
    iconEntities,
  };
};

export const parseCopySequenceCount = (value?: string | null) => {
  const match = value?.trim().match(/^x\s*(\d+)$/i);
  const countValue = match?.[1];
  if (!countValue) return null;

  const count = Number.parseInt(countValue, 10);
  if (!Number.isFinite(count) || count < 2) return null;
  return count;
};

export const duplicateCopiedEntitiesInSequence = (
  shapeSource: Shape[],
  dimensionSource: Dimension[],
  iconSource: CanvasIconEntity[],
  entityIds: string[],
  deltaX: number,
  deltaY: number,
  totalCopies: number,
  createPolylineId: () => string,
  createDimensionId: () => string,
  createIconId: () => string,
): DuplicateEntitiesResult => {
  if (totalCopies <= 1 || entityIds.length === 0) {
    return createEmptyDuplicateResult();
  }

  const selectedIdSet = new Set(entityIds);
  const sourceShapes = shapeSource.filter((shape) => selectedIdSet.has(shape.id));
  const sourceDimensions = dimensionSource.filter((dimension) => selectedIdSet.has(dimension.id));
  const sourceIcons = iconSource.filter((iconEntity) => selectedIdSet.has(iconEntity.id));
  const shapes: Shape[] = [];
  const dimensions: Dimension[] = [];
  const iconEntities: CanvasIconEntity[] = [];

  for (let copyIndex = 1; copyIndex < totalCopies; copyIndex += 1) {
    for (const shape of sourceShapes) {
      if (shape.kind === 'circle') {
        shapes.push({
          ...shape,
          id: createPolylineId(),
          center: translatePoint(shape.center, deltaX * copyIndex, deltaY * copyIndex),
        });
        continue;
      }

      if (shape.kind === 'arc') {
        shapes.push({
          ...shape,
          id: createPolylineId(),
          center: translatePoint(shape.center, deltaX * copyIndex, deltaY * copyIndex),
        });
        continue;
      }

      shapes.push({
        ...shape,
        id: createPolylineId(),
        points: translateCoordinateArray(shape.points, deltaX * copyIndex, deltaY * copyIndex),
        holes: shape.holes?.map((hole) =>
          translateCoordinateArray(hole, deltaX * copyIndex, deltaY * copyIndex),
        ),
        circleHoles: shape.circleHoles?.map((hole) =>
          translateCircleHole(hole, deltaX * copyIndex, deltaY * copyIndex),
        ),
        lifeLineVertices: translatePointArray(
          shape.lifeLineVertices,
          deltaX * copyIndex,
          deltaY * copyIndex,
        ),
        lifeLineDesignPoints: translatePointArray(
          shape.lifeLineDesignPoints,
          deltaX * copyIndex,
          deltaY * copyIndex,
        ),
      });
    }

    for (const dimension of sourceDimensions) {
      dimensions.push({
        ...dimension,
        id: createDimensionId(),
        start: translatePoint(dimension.start, deltaX * copyIndex, deltaY * copyIndex),
        end: translatePoint(dimension.end, deltaX * copyIndex, deltaY * copyIndex),
      });
    }

    for (const iconEntity of sourceIcons) {
      iconEntities.push({
        ...iconEntity,
        id: createIconId(),
        position: translatePoint(iconEntity.position, deltaX * copyIndex, deltaY * copyIndex),
      });
    }
  }

  return {
    entityIds: [
      ...shapes.map((shape) => shape.id),
      ...dimensions.map((dimension) => dimension.id),
      ...iconEntities.map((iconEntity) => iconEntity.id),
    ],
    shapes,
    dimensions,
    iconEntities,
  };
};
